import { useEffect, useRef, useCallback } from 'react';

interface ConfettiParticle {
  x: number;
  y: number;
  vy: number;
  color: string;
  size: number;
  alpha: number;
  rotation: number;
  vRotation: number;
}

const useConfetti = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const confettiParticles = useRef<ConfettiParticle[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const isAnimating = useRef(false);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    document.body.appendChild(canvas);
    canvasRef.current = canvas;
    ctxRef.current = canvas.getContext('2d');

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (canvasRef.current) document.body.removeChild(canvasRef.current);
    };
  }, []);

  const handleResize = () => {
    if (canvasRef.current) {
      canvasRef.current.width = window.innerWidth;
      canvasRef.current.height = window.innerHeight;
    }
  };

  const startConfetti = useCallback(() => {
    if (isAnimating.current) return;

    isAnimating.current = true;

    const startTime = Date.now();
    const duration = 4000;

    const spawnConfetti = () => {
      for (let i = 0; i < 10; i++) {
        confettiParticles.current.push({
          x: Math.random() * window.innerWidth,
          y: -10, // Start above the screen
          vy: Math.random() * 15 + 7.5, // speed
          color: `hsl(${Math.random() * 360}, 100%, 70%)`,
          size: Math.random() * 6 + 4,
          alpha: 1,
          rotation: Math.random() * 360,
          vRotation: Math.random() * 10 - 5, // Rotate slightly
        });
      }
    };

    const animate = () => {
      const ctx = ctxRef.current;
      const canvas = canvasRef.current;
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = confettiParticles.current.length - 1; i >= 0; i--) {
        const confetti = confettiParticles.current[i];
        confetti.y += confetti.vy;
        confetti.rotation += confetti.vRotation;
        confetti.alpha -= 0.005; // Slow fade

        ctx.save();
        ctx.globalAlpha = confetti.alpha;
        ctx.fillStyle = confetti.color;
        ctx.translate(confetti.x, confetti.y);
        ctx.rotate((confetti.rotation * Math.PI) / 180);
        ctx.fillRect(
          -confetti.size / 2,
          -confetti.size / 2,
          confetti.size,
          confetti.size,
        );
        ctx.restore();

        if (confetti.alpha <= 0) {
          confettiParticles.current.splice(i, 1);
        }
      }

      if (Date.now() - startTime < duration) {
        spawnConfetti();
      }

      if (confettiParticles.current.length > 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimating.current = false;
      }
    };

    animate();
  }, []);

  return { startConfetti };
};

export default useConfetti;
